/**
 * Dashboard Error State Component
 *
 * Displays error messages and retry options for dashboard sections
 */

import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface DashboardErrorStateProps {
  title: string;
  message: string;
  onRetry?: () => void;
  lastUpdated?: string;
}

/**
 * Error state component for dashboard sections
 */
export default function DashboardErrorState({
  title,
  message,
  onRetry,
  lastUpdated,
}: DashboardErrorStateProps) {
  return (
    <Card className="p-6" role="alert">
      <div className="flex flex-col items-center justify-center space-y-4 text-center">
        <div className="rounded-full bg-red-100 p-3">
          <AlertCircle className="h-6 w-6 text-red-600" />
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600">{message}</p>
          {lastUpdated && <p className="text-xs text-gray-500">Last updated: {lastUpdated}</p>}
        </div>

        {onRetry && (
          <Button
            variant="outline"
            onClick={onRetry}
            className="mt-2"
            aria-label="Retry loading data"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        )}
      </div>
    </Card>
  );
}

/**
 * Inline error message for smaller sections
 */
export function InlineErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div
      className="flex items-center justify-between rounded-md border border-red-200 bg-red-50 p-4"
      role="alert"
    >
      <div className="flex items-center space-x-3">
        <AlertCircle className="h-5 w-5 text-red-600" />
        <p className="text-sm text-red-800">{message}</p>
      </div>
      {onRetry && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRetry}
          className="text-red-600 hover:text-red-700"
          aria-label="Retry"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
